// Grade level convention shared with the backend (apps/api/src/db/schemas/pg.ts):
// -1 = Pre-K, 0 = Kindergarten, 1-12 = standard grades.
//
// The *integer* is the convention; the *word* is the school's. Every function here
// takes an optional `labels` map - the school's configured grade levels, from
// `useGradeLevels` - and falls back to the built-in wording without it. The fallback
// is not a placeholder to be removed later: these are pure functions used in tests,
// in search-text builders and in components that render before the query resolves,
// and all of them need an answer before the school's own words have arrived.

import { NO_IDENTIFIER } from './identifiers';

/** `level -> the school's word for it`, from `/api/grade-levels`. */
export type GradeLevelLabels = ReadonlyMap<number, string>;

/**
 * A student's graduation year where the year is the subject of its own cell,
 * labelled row or stat tile.
 *
 * The year is optional - nobody enrolling a Pre-K child knows it, and the record
 * no longer makes one up - so every screen that prints it needs an answer for the
 * blank. An em dash, the same one `lib/identifiers.ts` uses and for the same
 * reason: the row keeps its place in the grid, and an empty cell under a
 * "Graduation" header reads as a rendering fault rather than as "not recorded".
 *
 * Where the year is *decoration* inside a longer sentence, drop the clause
 * instead; this helper is only for the standalone case.
 */
export function graduationYearOrDash(year: number | null | undefined): string {
  return year == null ? NO_IDENTIFIER : String(year);
}

export function gradeLevelLabel(level: number, labels?: GradeLevelLabels): string {
  const configured = labels?.get(level);
  if (configured) return configured;
  if (level === -1) return 'Pre-K';
  if (level === 0) return 'K';
  return `Grade ${level}`;
}

/**
 * Formats a course's grade levels for display. An empty/missing array means the course
 * has no grade-level restriction (open to all grades, or a mixed-grade course that
 * hasn't been configured yet) rather than "no grades" - so it reads as "All grades".
 */
export function formatGradeLevels(
  levels: number[] | null | undefined,
  labels?: GradeLevelLabels
): string {
  if (!levels || levels.length === 0) return 'All grades';
  return levels
    .slice()
    .sort((a, b) => a - b)
    .map((level) => gradeLevelLabel(level, labels))
    .join(', ');
}

/**
 * A course's grade span, short enough to sit in a sidebar subtitle: consecutive
 * grades collapse to a range ("Grades 7–8"), a span with a hole in it lists its
 * grades ("Grades 7, 9"). Spans reaching below Grade 1 spell every grade out,
 * because "Grades Pre-K–1" does not read as English.
 */
export function formatGradeLevelSpan(
  levels: number[] | null | undefined,
  labels?: GradeLevelLabels
): string {
  if (!levels || levels.length === 0) return 'All grades';

  const sorted = [...new Set(levels)].sort((a, b) => a - b);
  if (sorted.length === 1) return gradeLevelLabel(sorted[0], labels);
  if (sorted[0] < 1) return sorted.map((level) => gradeLevelLabel(level, labels)).join(', ');

  // "Grades 7-8" is a contraction of "Grade 7" and "Grade 8", so it is only honest
  // while those are what the grades are called. A school that renamed them to
  // "Year 7" and "Year 8" gets its own words listed rather than a range built out
  // of a noun it does not use.
  const usesDefaultWording = sorted.every(
    (level) => gradeLevelLabel(level, labels) === gradeLevelLabel(level)
  );
  if (!usesDefaultWording) {
    return sorted.map((level) => gradeLevelLabel(level, labels)).join(', ');
  }

  const isConsecutive = sorted.every((level, i) => i === 0 || level === sorted[i - 1] + 1);
  return isConsecutive
    ? `Grades ${sorted[0]}–${sorted[sorted.length - 1]}`
    : `Grades ${sorted.join(', ')}`;
}

/** A course-shaped record: anything carrying grade levels can be grouped. */
export interface HasGradeLevels {
  gradeLevels?: number[] | null;
  /** @deprecated Legacy single grade level, still set on unmigrated rows. */
  gradeLevel?: number | null;
}

export interface GradeSpanGroup<T> {
  /** Stable React key; distinct per span and for the unrestricted group. */
  key: string;
  label: string;
  /** The grades the group covers, ascending; empty for the unrestricted group. */
  levels: number[];
  items: T[];
}

/**
 * The grades a course is actually taught in, tolerating unmigrated rows that
 * carry only the legacy singular `gradeLevel`. An empty result means the course
 * is restricted to no grade. Mirrors `effectiveGradeLevels` in
 * apps/api/src/services/courseEligibility.ts, which owns the rule.
 *
 * Exported despite that: this was private, on the reasoning that the frontend
 * should not advertise a second source of truth. It ended up doing exactly that
 * anyway — callers that needed the levels rather than a formatted string
 * hand-rolled the fallback inline, and one copy got it wrong (`gradeLevels ??
 * …` keeps an empty array, so a row with `[]` and a legacy scalar resolved to
 * "no grades" here and to the scalar everywhere else). One exported copy of the
 * rule is fewer than one private copy plus the ones it fails to prevent.
 */
export function effectiveGradeLevels(course: HasGradeLevels): number[] {
  if (course.gradeLevels && course.gradeLevels.length > 0) return course.gradeLevels;
  if (course.gradeLevel != null) return [course.gradeLevel];
  return [];
}

/**
 * A course's grade levels as a Grade Levels cell shows them — "Grade 7, Grade 8",
 * or "All grades" for a course with no restriction.
 *
 * This is the one every course table calls, rather than `formatGradeLevels` on a
 * raw array: it applies the legacy-scalar fallback first, so an unmigrated row
 * reads as the grade it is actually taught in instead of "All grades", which is
 * the opposite of the truth. Every grade is spelled out rather than collapsed to
 * a range, because a cell is read one row at a time and "Grades 7, 9" must not
 * be mistaken for "Grades 7–9"; `formatGradeLevelSpan` is the collapsing one,
 * for a heading that has the rows underneath it for context.
 *
 * `labels` is the school's own wording, as everywhere else in this module — a
 * school that calls them "Year 7" gets that in the cell too. Optional for the
 * same reason: a search-text builder or a component rendering before the query
 * resolves still needs an answer.
 */
export function formatCourseGradeLevels(
  course: HasGradeLevels,
  labels?: GradeLevelLabels
): string {
  return formatGradeLevels(effectiveGradeLevels(course), labels);
}

/**
 * Orders course rows by the grades they are taught in: lowest grade first, so
 * ascending runs up the school. Sorting on the formatted label instead would
 * put "Grade 10" before "Grade 9".
 *
 * A course with no grade-level restriction sorts last, matching where the
 * "All grades" band sits in `groupByGradeSpan`.
 */
export function gradeLevelSortValue(course: HasGradeLevels): number {
  const levels = effectiveGradeLevels(course);
  if (levels.length === 0) return Number.MAX_SAFE_INTEGER;
  return Math.min(...levels);
}

/** Lowest grade first, then shortest span; the unrestricted group sorts last. */
function compareSpans(a: number[], b: number[]): number {
  if (a.length === 0) return b.length === 0 ? 0 : 1;
  if (b.length === 0) return -1;

  const bySpan = a[0] - b[0] || a[a.length - 1] - b[b.length - 1] || a.length - b.length;
  if (bySpan !== 0) return bySpan;

  // Two gappy spans can share their ends and their size ("7, 8, 10, 11" and
  // "7, 9, 10, 11"); compare grade by grade so their order is the data's, not
  // whichever course the API happened to list first.
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

/**
 * Buckets courses by the span of grades they are taught in — "Grades 7–8",
 * "Grades 9–10" — in grade order, with courses restricted to no grade collected
 * into a trailing "All grades" group.
 *
 * Grouping by the span rather than by each individual grade is what this data
 * calls for: courses here are written per band (Math 7-8, Visual Arts 9-10), so
 * a group per grade would list every course twice and make Grade 7 and Grade 8
 * carbon copies of each other. One group per band lists each course once and
 * matches how the school actually divides its years.
 */
export function groupByGradeSpan<T extends HasGradeLevels>(
  items: T[],
  labels?: GradeLevelLabels
): GradeSpanGroup<T>[] {
  const groups = new Map<string, GradeSpanGroup<T>>();

  for (const item of items) {
    // Normalized, so [8, 7] and [7, 8] are the same band rather than two.
    const levels = [...new Set(effectiveGradeLevels(item))].sort((a, b) => a - b);
    const key = levels.length > 0 ? `grades-${levels.join('-')}` : 'all-grades';

    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { key, label: formatGradeLevelSpan(levels, labels), levels, items: [item] });
    }
  }

  return [...groups.values()].sort((a, b) => compareSpans(a.levels, b.levels));
}
